import { combineReducers } from 'redux';

import { ProposalStatsAction, ProposalStatsActionKeys } from './actions';
import { Api } from '../../typings/api';

const ITEMS_PER_PAGE = 20;

interface PaginatorState {
	itemsPerPage: number;
	page: number;
	totalPages: number;
}

export enum SortOn {
	TYPE = 'TYPE',
	LABEL = 'LABEL',
	SENT = 'SENT',
	READ = 'READ',
	OPENED = 'OPENED',
}

export interface SortState {
	sortOn: SortOn;
	ascending: boolean;
}

export interface ProposalStatsState {
	isLoading: boolean;
	proposalStatus: Api.ProposalStatus;
	paginator: PaginatorState;
	byId: { [k: string]: Api.AspDriveResourceItem };
	allIds: string[];
	sort: SortState;
}

const proposalStatusInitialState = Api.ProposalStatus.SEEN;

const paginatorInitialState = {
	itemsPerPage: ITEMS_PER_PAGE,
	page: 0,
	totalPages: 1,
};

const sortInitialState = {
	[Api.ProposalStatus.SEEN]: { sortOn: SortOn.OPENED, ascending: false },
	[Api.ProposalStatus.SENT]: { sortOn: SortOn.SENT, ascending: false },
};

// only combination of item's globalId and sendingDate is guaranteed to be unique
// because one item can be sent several times
const getItemId = ({ globalId, sendingDate }: Api.AspDriveResourceItem) => (
	`${globalId}-${sendingDate}`
);

const isLoading = (state = false, action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.SET_IS_LOADING:
			return action.isLoading;
		case ProposalStatsActionKeys.RESET:
			return false;
		default:
			return state;
	}
};

const proposalStatus = (state = proposalStatusInitialState, action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.SET_PROPOSAL_STATUS:
			return action.status;
		case ProposalStatsActionKeys.RESET:
			return proposalStatusInitialState;
		default:
			return state;
	}
};

const paginator = (state = paginatorInitialState, action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.SET_PAGINATOR_DATA:
			return {
				...state,
				...action.data,
			};
		case ProposalStatsActionKeys.RESET:
			return paginatorInitialState;
		default:
			return state;
	}
};

const byId = (state = {}, action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.ADD_PAGE:
			return {
				...state,
				...action.list.reduce((byId, item) => {
					const id = getItemId(item);
					byId[id] = item;
					return byId;
				}, {}),
			};
		case ProposalStatsActionKeys.SET_NEW_PAGE:
		case ProposalStatsActionKeys.RESET:
			return {};
		default:
			return state;
	}
};

const allIds = (state = [], action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.ADD_PAGE:
			return [
				...state,
				...action.list.map(getItemId),
			];
		case ProposalStatsActionKeys.SET_NEW_PAGE:
		case ProposalStatsActionKeys.RESET:
			return [];
		default:
			return state;
	}
};

const sort = (state = sortInitialState[proposalStatusInitialState], action: ProposalStatsAction) => {
	switch (action.type) {
		case ProposalStatsActionKeys.SORT:
			return action.sort;
		case ProposalStatsActionKeys.SET_PROPOSAL_STATUS:
			return sortInitialState[action.status];
		case ProposalStatsActionKeys.RESET:
			return sortInitialState[proposalStatusInitialState];
		default:
			return state;
	}
};

export const proposalStats = combineReducers({
	isLoading,
	proposalStatus,
	paginator,
	byId,
	allIds,
	sort,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ProposalStatsPage/reducer.ts